import { parseAttachmentFilename, toReadableDownloadError } from '../attachment-response'
import { apiClient } from '../config'

import type { CreateOrderRequest, GetOrdersParams, JunctionRelinkCandidatesResponse, Order, OrderAnalytics, OrderStatus, PaginatedOrdersResponse, LabRecommendationsAdminResponse } from '../types'

const RESULTS_PDF_DOWNLOAD_TIMEOUT_MS = 300_000

export const ordersService = {
  getOrders: async (params: GetOrdersParams = {}, options?: { signal?: AbortSignal }): Promise<PaginatedOrdersResponse> => {
    const response = await apiClient.get<PaginatedOrdersResponse>('/admin/orders', { params, ...(options?.signal ? { signal: options.signal } : {}) })
    return response.data
  },

  getOrderAnalytics: async (): Promise<OrderAnalytics> => {
    const response = await apiClient.get<OrderAnalytics>('/admin/orders/analytics')
    return response.data
  },

  getUserIdsWithOrders: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>('/admin/orders/user-ids')
    return response.data
  },

  createOrder: async (data: CreateOrderRequest): Promise<Order> => {
    const response = await apiClient.post<Order>('/admin/orders', data)
    return response.data
  },

  getOrderById: async (id: string): Promise<Order> => {
    const response = await apiClient.get<Order>(`/admin/orders/${id}`)
    return response.data
  },

  generateRecommendations: async (orderId: string): Promise<void> => {
    await apiClient.post(`/admin/orders/${orderId}/generate-recommendations`)
  },

  getOrderRecommendations: async (orderId: string): Promise<LabRecommendationsAdminResponse> => {
    const response = await apiClient.get<LabRecommendationsAdminResponse>(`/admin/orders/${orderId}/recommendations`)
    return response.data
  },

  downloadResultsPdf: async (orderId: string): Promise<{ blob: Blob; filename: string }> => {
    let response
    try {
      response = await apiClient.get<Blob>(`/admin/orders/${orderId}/results/pdf`, {
        responseType: 'blob',
        timeout: RESULTS_PDF_DOWNLOAD_TIMEOUT_MS,
      })
    } catch (error) {
      throw await toReadableDownloadError(error)
    }

    return {
      blob: response.data,
      filename: parseAttachmentFilename({
        contentDisposition: response.headers['content-disposition'],
        fallbackFilename: `lab-results-${orderId}.pdf`,
      }),
    }
  },

  deleteOrder: async (orderId: string): Promise<Order> => {
    const response = await apiClient.delete<Order>(`/admin/orders/${orderId}`)
    return response.data
  },

  updateOrder: async (orderId: string, updateData: {
    packageId?: string;
    status?: OrderStatus;
    junctionOrderId?: string;
  }): Promise<Order> => {
    const response = await apiClient.patch<Order>(`/admin/orders/${orderId}`, updateData)
    return response.data
  },

  resendLabResultsNotification: async (orderId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/admin/orders/${orderId}/resend-lab-results-notification`)
    return response.data
  },

  resendReportEmail: async (orderId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/admin/orders/${orderId}/resend-report-email`)
    return response.data
  },

  resendPurchaseConfirmation: async (orderId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/admin/orders/${orderId}/resend-purchase-confirmation`)
    return response.data
  },

  resendOnboardingReminder: async (orderId: string): Promise<{ message: string }> => {
    const response = await apiClient.post<{ message: string }>(`/admin/orders/${orderId}/resend-onboarding-reminder`)
    return response.data
  },

  getJunctionRelinkCandidates: async (orderId: string): Promise<JunctionRelinkCandidatesResponse> => {
    const response = await apiClient.get<JunctionRelinkCandidatesResponse>(`/admin/orders/${orderId}/junction-candidates`)
    return response.data
  },

  relinkJunctionOrder: async (orderId: string, junctionOrderId: string): Promise<Order> => {
    const response = await apiClient.post<Order>(`/admin/orders/${orderId}/relink-junction`, { junctionOrderId })
    return response.data
  },
}
